import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import httpInstances from '@/api/axiosInstance';
import { analytics } from '@/services/analytics';
import { clearStoragesFromTokensAndUserInfo } from '@/services/storage';
import type { ApiErrorPayload, ErrorResponse } from '@/types/errors';
import { getInternalBasePath } from '@/utils/environmentPaths';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { handleThunkErrorWithStatus } from '@/utils/thunkErrorHandler';

export type AuthApiState = {
  status: 'idle' | 'loading' | 'failed' | 'fulfilled' | 'notFound' | 'onSuppressionList';
  error: string | null;
};

type LoginApiErrorResponse = {
  errors?: string[];
};

const initialState: AuthApiState = {
  status: 'idle',
  error: null
};

export const loginGX = createAsyncThunk(
  'loginGX',
  async (request: { email: string; captchaToken: string }, { rejectWithValue }) => {
    try {
      await httpInstances.axiosInternalInstance.post(
        `${getInternalBasePath()}login`,
        { email: request.email },
        { headers: { captchaToken: request.captchaToken } }
      );
    } catch (error) {
      try {
        return handleThunkErrorWithStatus(error, rejectWithValue) as never;
      } catch (e) {
        analytics.captureLoginError(error);
        throw e;
      }
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetStatus(state) {
      state.status = 'idle';
    },
    logout() {
      clearStoragesFromTokensAndUserInfo();
      analytics.reset();
      return initialState;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loginGX.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginGX.fulfilled, state => {
        state.status = 'fulfilled';
      })
      .addCase(loginGX.rejected, (state, action) => {
        const response = action.payload as ApiErrorPayload | undefined;

        if (response?.status === 404) {
          state.status = 'notFound';
        } else if (
          response?.status === 403 &&
          (response.data as LoginApiErrorResponse).errors?.some(error =>
            error.includes('is on suppression list')
          )
        ) {
          state.status = 'onSuppressionList';
        } else {
          analytics.captureLoginRejection(action.payload);
          state.status = 'failed';
          const data = response?.data as ErrorResponse | undefined;
          state.error = extractErrorMessage(
            data?.message ? data : null,
            action.error.message,
            'Login failed'
          );
        }
      });
  }
});

export const { resetStatus, logout } = authSlice.actions;

export default authSlice.reducer;
